// src/api/client.js
import axios from 'axios';
import { clearAuthSessionStorage, getAuthToken } from '../utils/authSession';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  withCredentials: false, // token-only flow
});

function shouldSkipAuthRedirect(config = {}) {
  const url = String(config?.url || '');
  const method = String(config?.method || '').toLowerCase();
  return (
    config?.skipAuthRedirect === true ||
    url.includes('/auth/login') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password') ||
    url.includes('/auth/register') ||
    (method === 'post' && url.includes('/auth/login'))
  );
}

function isBootstrapSessionCall(config = {}) {
  const url = String(config?.url || '');
  const method = String(config?.method || '').toLowerCase();
  return (
    method === 'get' &&
    (
      url.includes('/me/session') ||
      url.includes('/me/enterprise') ||
      url.includes('/me/token')
    )
  );
}

function getAuthLoginUrl() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  const authBase = envBase || (
    typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    )
      ? 'http://localhost:3000'
      : 'https://auth.sushaservices.com'
  );
  // Do not import the application route registry here. The registry imports
  // page modules, which in turn use this API client; importing it creates a
  // circular dependency during application startup.
  return `${authBase}/login`;
}

function getAuthLogoutUrl() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  const authBase = envBase || (
    typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1'
    )
      ? 'http://localhost:3000'
      : 'https://auth.sushaservices.com'
  );
  return `${authBase}/logout`;
}

function debugClientLog(event, payload = {}) {
  try {
    console.log(`[itam-client-debug] ${event}`, payload);
  } catch {
    console.log(`[itam-client-debug] ${event}`);
  }
}

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (isBootstrapSessionCall(config)) {
    debugClientLog('bootstrap.request', {
      method: config?.method,
      url: config?.url,
      hasToken: Boolean(token),
    });
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    if (isBootstrapSessionCall(res?.config)) {
      debugClientLog('bootstrap.response', {
        method: res?.config?.method,
        url: res?.config?.url,
        status: res?.status,
        hasData: Boolean(res?.data),
      });
    }
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    const url = String(err?.config?.url || '');
    const hasToken = Boolean(getAuthToken());

    if (isBootstrapSessionCall(err?.config)) {
      debugClientLog('bootstrap.error', {
        method: err?.config?.method,
        url,
        status,
        hasToken,
        responseError: err?.response?.data?.error || err?.response?.data?.message || err?.message || null,
      });
    }

    if (status === 401 && isBootstrapSessionCall(err?.config) && hasToken) {
      // Keep the callback session alive if the enterprise bootstrap call
      // fails before the app has fully settled. The page can still render
      // from the callback payload while we debug the backend token issue.
      console.warn('[auth-bootstrap] session bootstrap 401', {
        url,
        method: err?.config?.method,
      });
      return Promise.reject(err);
    }

    if (status === 401 && !shouldSkipAuthRedirect(err?.config)) {
      // clear and bounce to login
      clearAuthSessionStorage();
      window.location.href = getAuthLogoutUrl();
    }
    return Promise.reject(err);
  }
);
